import { useState } from 'react';
import { ImageOff, Paperclip } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SocialMediaSnapshotRef } from '../types';

/**
 * The attachment the agent actually looked at, as stored by us rather than as linked by Meta.
 *
 * Both surfaces render through this so the thumbnail in the table and the image in the panel
 * cannot drift apart in what they call a failure. The distinction they share, and the reason this
 * is not just an `<img>`:
 *
 *  - a row with NO refs had no attachment — draw nothing, and no gap either. Most comments are
 *    words and a placeholder on every one of them is noise.
 *  - a row WITH a ref but no `url` means the server could not sign that object. The comment did
 *    carry media and we cannot show it, which is a gap in the record worth admitting.
 *  - a `url` that fails to load is the same admission arrived at later — usually an expired
 *    signature on a page left open.
 */

/**
 * Marks the tile as a GIF.
 *
 * `contentType` is the real answer once the capture has unwrapped Meta's proxy and stored the
 * animated file. The `giphy.com` check is the fallback's: when the original could not be reached
 * the bytes are the single JPEG frame Meta rendered, and the badge is then the only thing saying
 * why a GIF sits still. It also covers every row captured before the unwrap existed.
 */
const isAnimated = (ref: SocialMediaSnapshotRef): boolean =>
	ref.contentType === 'image/gif' || ref.sourceUrl.includes('giphy.com');

/** Private: exporting a non-component from this file would break fast refresh. */
const capturedRefs = (refs: SocialMediaSnapshotRef[] | null | undefined): SocialMediaSnapshotRef[] => refs ?? [];

/**
 * The table's 24px tile. Sized to the row's 40px of content, so adding it changes no heights.
 *
 * Only the FIRST attachment is drawn. A row can carry several and the column is a way of
 * recognising the row, not of reading it; the panel shows them all.
 */
export const CapturedMediaThumb = ({ refs }: { refs: SocialMediaSnapshotRef[] | null | undefined }) => {
	const [failed, setFailed] = useState(false);
	const first = capturedRefs(refs)[0];

	if (!first) return null;

	const unavailable = failed || !first.url;

	return (
		<span
			className={cn(
				'relative grid size-6 shrink-0 place-items-center overflow-hidden rounded border border-gray-400 bg-gray-100',
				unavailable && 'border-dashed'
			)}
			title={unavailable ? 'The attachment could not be loaded' : 'Attachment captured with this comment'}
		>
			{unavailable ? (
				<ImageOff className='size-3 text-gray-700' aria-label='Attachment unavailable' />
			) : (
				<>
					<img
						src={first.url}
						alt=''
						className='size-full object-cover'
						loading='lazy'
						onError={() => setFailed(true)}
					/>
					{isAnimated(first) && (
						// A still frame of a GIF looks like a photo that will not play; the badge says why.
						<span className='absolute right-0 bottom-0 rounded-tl-[3px] bg-black/65 px-[2px] text-[6.5px] leading-[9px] font-bold text-white'>
							GIF
						</span>
					)}
				</>
			)}
		</span>
	);
};

/** One captured attachment, full width of the bubble. */
const PanelImage = ({ media }: { media: SocialMediaSnapshotRef }) => {
	const [failed, setFailed] = useState(false);
	const unavailable = failed || !media.url;

	return (
		<figure className='m-0 max-w-[200px] overflow-hidden rounded-lg border border-border bg-gray-100'>
			{unavailable ? (
				<div className='flex h-[104px] flex-col items-center justify-center gap-1 px-3 text-center'>
					<ImageOff className='size-4 text-gray-700' aria-hidden='true' />
					<span className='text-[11px] text-gray-800'>This attachment could not be loaded</span>
				</div>
			) : (
				<img src={media.url} alt='' className='block h-auto w-full' onError={() => setFailed(true)} />
			)}

			{/* Provenance, not decoration: this is evidence, so what it is and when we took it belong
			    with it. `capturedAt` is ours — the moment the bytes were copied, which is the only
			    timestamp that survives the comment being deleted. */}
			<figcaption className='flex items-center justify-between gap-2 border-t border-border px-2 py-1 text-[10.5px] text-gray-800'>
				<span className='inline-flex items-center gap-1'>
					<Paperclip className='size-2.5' aria-hidden='true' />
					{isAnimated(media) ? 'GIF' : 'Image'} · {Math.max(1, Math.round(media.bytes / 1024))} KB
				</span>
				<span>captured {new Date(media.capturedAt).toLocaleDateString()}</span>
			</figcaption>
		</figure>
	);
};

/** Every captured attachment on the row, stacked under the comment text. */
export const CapturedMedia = ({ refs }: { refs: SocialMediaSnapshotRef[] | null | undefined }) => {
	const all = capturedRefs(refs);
	if (all.length === 0) return null;

	return (
		<div className='mt-1.5 flex flex-col gap-1.5'>
			{/* Index as the React key: the server no longer sends the S3 key, and nothing else on a
			    ref is unique — two identical attachments on one comment share every field. The list
			    is static for the life of the panel, so the usual objection does not apply. */}
			{all.map((media, index) => (
				<PanelImage key={index} media={media} />
			))}
		</div>
	);
};
